import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { customFormatters } from './customFormatters';
import lazyi18nextBackend from './lazyI18nextBackend';
import ReactPostprocessor from 'i18next-react-postprocessor';
import featureBrancher, {
  setupI18nextFeatureBranching,
} from './featureBrancherPostProcessor';
import unusedKeyBlocker, {
  setupUnusedKeyBlocking,
} from './unusedKeyBlockerPostProcessor';
import { SUPPORTED_LANGUAGES } from './constants';

/**
 * This is the default i18next config used by frontend projects
 *
 * You can provide either single resource or a map of ns to resources
 */
export default function i18n(
  { locale, resources, options = {} },
  debug?: boolean,
  flags?: string[] | null
) {
  // Create resources object with locale as key and translation namespace
  const res = { [locale]: { translation: resources } };

  // Allow NS resources to be passed - if resources already has translation namespace structure
  if (resources.translation) {
    res[locale] = resources;
  }

  // Setup feature branching for conditional translations based on feature flags
  setupI18nextFeatureBranching(flags, i18next);

  // Setup unused key blocking for canary testing i18n key deletion.
  // This audit is intentionally client-only and is not registered in server presets.
  setupUnusedKeyBlocking(flags);

  return (
    i18next
      // Initialize React i18next integration for React components
      .use(initReactI18next)
      // Use lazy loading backend for code-splitting translation resources
      .use(lazyi18nextBackend())
      // Enable React post-processor for JSX interpolation in translations
      .use(new ReactPostprocessor())
      // Enable featureBrancher for feature flag-based translations
      .use(featureBrancher)
      // Enable unusedKeyBlocker for client-side blocked-key auditing only.
      .use(unusedKeyBlocker)
      // Enable custom formatters for advanced translation formatting
      .use(customFormatters)
      .init(
        {
          // Allow mixing of preset bundled resources with dynamically loaded ones
          partialBundledLanguages: true,

          // Use '$' as separator for nested translation keys to avoid conflicts with file paths
          keySeparator: '$',

          // Use the provided locale as fallback language
          fallbackLng: locale,

          // Enable debug mode for development - logs detailed i18next information
          debug,

          // Don't escape interpolated values to allow React components in translations
          interpolation: {
            escapeValue: false,
          },

          // Enable featureBrancher post-processor for feature flag-based translations
          postProcess: ['featureBrancher', 'unusedKeyBlocker'],

          // Use the prepared resources object with locale and translation data
          resources: res,

          // Preload all supported languages in production for better performance, load on-demand in dev
          // preload: SUPPORTED_LANGUAGES,

          // Restrict to only these supported language codes
          supportedLngs: SUPPORTED_LANGUAGES,

          // Normalize language codes to base language only (en-GB -> en, es-MX -> es)
          load: 'languageOnly',

          // Modern app safety defaults
          // Remove language codes from URLs when they match fallback language
          cleanCode: true,

          // Convert all language codes to lowercase for consistency
          lowerCaseLng: true,

          // Allow implicit language variants not explicitly in supportedLngs (en-GB -> en)
          nonExplicitSupportedLngs: true,

          // Allow additional options to override any of the above settings
          ...options,
        },
        undefined
      )
  );
}
